import { useCallback, useEffect, useRef, useState } from "react";
import { W } from "../engine/constants";

function requestNativeFullscreen(el) {
  // Try every browser prefix. Safari desktop uses webkit; old Edge uses ms.
  // iOS Safari on iPhone does NOT support Element.requestFullscreen for
  // anything other than <video>, so every variant will reject — we fall back
  // to a CSS-based "faux fullscreen" (see toggleFullscreen below).
  const fn =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.msRequestFullscreen;
  if (!fn) return Promise.reject(new Error("fullscreen-unsupported"));
  try {
    const p = fn.call(el);
    return p && typeof p.then === "function" ? p : Promise.resolve();
  } catch (e) {
    return Promise.reject(e);
  }
}

function exitNativeFullscreen() {
  const fn =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.msExitFullscreen;
  if (!fn) return Promise.resolve();
  try {
    const p = fn.call(document);
    return p && typeof p.then === "function" ? p : Promise.resolve();
  } catch (_) {
    return Promise.resolve();
  }
}

function currentFullscreenEl() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement ||
    null
  );
}

// Wires up keyboard, mouse, touch, pointer-lock and fullscreen behaviour for
// the game canvas. Mutates the supplied refs (keysRef / mouseXRef) so the
// render loop can read them without re-renders.
export default function useGameInput({
  canvasRef,
  wrapperRef,
  fsTargetRef,
  keysRef,
  mouseXRef,
  onLaunch,
  onTogglePause,
  onRestart,
  onQuit,
  onOpenSkins,
  stopLoopOn, // { gameOver, won } — release pointer lock when true
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pointerLocked, setPointerLocked] = useState(false);
  // When native Fullscreen API is unavailable (iOS Safari on iPhone), we flip
  // a CSS class on the wrapper and track that state here.
  const fauxFsRef = useRef(false);

  // Keyboard
  useEffect(() => {
    const keydown = (e) => {
      // Let typing inside inputs/textareas (e.g. Legend filter) pass through
      // untouched — hotkeys only apply when the pilot isn't composing text.
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) {
        return;
      }
      keysRef.current[e.key] = true;
      if (e.key === " " || e.key === "Enter") onLaunch();
      if (e.key === "p" || e.key === "P" || e.key === "Escape") onTogglePause();
      // Fullscreen action hotkeys. R/K/Q are the three right-side buttons;
      // all three are cheap no-ops when the corresponding handler isn't
      // passed, so this block stays safe across the regular-play and
      // endless-mode call sites.
      if ((e.key === "r" || e.key === "R") && onRestart) {
        e.preventDefault();
        onRestart();
      }
      if ((e.key === "k" || e.key === "K") && onOpenSkins) {
        e.preventDefault();
        onOpenSkins();
      }
      if ((e.key === "q" || e.key === "Q") && onQuit) {
        e.preventDefault();
        onQuit();
      }
      if ([" ", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault();
      }
    };
    const keyup = (e) => {
      keysRef.current[e.key] = false;
    };
    window.addEventListener("keydown", keydown);
    window.addEventListener("keyup", keyup);
    return () => {
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("keyup", keyup);
    };
  }, [keysRef, onLaunch, onTogglePause, onRestart, onQuit, onOpenSkins]);

  // Release pointer lock when the game ends
  useEffect(() => {
    if ((stopLoopOn.gameOver || stopLoopOn.won) && document.pointerLockElement) {
      try {
        document.exitPointerLock?.();
      } catch (_) {
        /* noop */
      }
    }
  }, [stopLoopOn.gameOver, stopLoopOn.won]);

  // Native fullscreen + pointer-lock change listeners (incl. webkit prefix for Safari)
  useEffect(() => {
    const onFsChange = () => {
      const active = !!currentFullscreenEl() || fauxFsRef.current;
      setIsFullscreen(active);
      if (!currentFullscreenEl() && !fauxFsRef.current) {
        try {
          document.exitPointerLock?.();
        } catch (_) {
          /* noop */
        }
      }
    };
    // Sync initial state — important when GameCanvas remounts inside a
    // still-fullscreened ancestor (e.g., clicking "Next Sector").
    onFsChange();
    const onPlChange = () => {
      setPointerLocked(document.pointerLockElement != null);
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    document.addEventListener("msfullscreenchange", onFsChange);
    document.addEventListener("pointerlockchange", onPlChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
      document.removeEventListener("msfullscreenchange", onFsChange);
      document.removeEventListener("pointerlockchange", onPlChange);
    };
  }, []);

  // Exit faux fullscreen when the user hits Escape (matches native UX)
  useEffect(() => {
    if (!isFullscreen || !fauxFsRef.current) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        fauxFsRef.current = false;
        setIsFullscreen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(async () => {
    // Prefer a stable parent (fsTargetRef) provided by the route so that
    // React remounts of GameCanvas (e.g., clicking "Next Sector" which swaps
    // the level) don't drop native fullscreen. Fall back to the canvas
    // wrapper when no stable target is supplied (e.g., Daily, Playtest).
    const el = (fsTargetRef && fsTargetRef.current) || wrapperRef.current;
    if (!el) return;
    const canvas = canvasRef.current;
    const isTouch =
      typeof window !== "undefined" &&
      ("ontouchstart" in window || navigator.maxTouchPoints > 0);

    // Already fullscreen (native or faux) — exit.
    if (currentFullscreenEl() || fauxFsRef.current) {
      try {
        await exitNativeFullscreen();
      } catch (_) {
        /* noop */
      }
      try {
        window.screen?.orientation?.unlock?.();
      } catch (_) {
        /* noop */
      }
      if (fauxFsRef.current) {
        fauxFsRef.current = false;
        setIsFullscreen(false);
      }
      return;
    }

    // Try native first (works on Android Chrome + desktop). On iOS Safari this
    // rejects — catch it and flip the CSS fallback.
    try {
      await requestNativeFullscreen(el);
    } catch (_) {
      fauxFsRef.current = true;
      setIsFullscreen(true);
    }

    // Best-effort: lock to landscape for a 4:3 game on phones. Not all
    // browsers allow this outside of a locked fullscreen session; just try
    // and swallow failures.
    if (isTouch) {
      try {
        await window.screen?.orientation?.lock?.("landscape");
      } catch (_) {
        /* noop */
      }
    } else {
      try {
        await canvas?.requestPointerLock?.({ unadjustedMovement: true });
      } catch (_) {
        try {
          canvas?.requestPointerLock?.();
        } catch (__) {
          /* noop */
        }
      }
    }
  }, [wrapperRef, canvasRef, fsTargetRef]);

  // Pointer + touch handlers
  const onMouseMove = (e) => {
    if (document.pointerLockElement === canvasRef.current) {
      const sensitivity = 1.4;
      const delta = (e.movementX || 0) * sensitivity;
      const cur = mouseXRef.current ?? W / 2;
      mouseXRef.current = Math.max(0, Math.min(W, cur + delta));
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = W / rect.width;
    mouseXRef.current = (e.clientX - rect.left) * scaleX;
  };
  const onClick = () => onLaunch();
  const onMouseDown = (e) => {
    if (e.button === 2) {
      e.preventDefault();
      onLaunch();
    }
  };
  const onContextMenu = (e) => {
    e.preventDefault();
    onLaunch();
  };
  const onTouchMove = (e) => {
    if (!e.touches?.[0]) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = W / rect.width;
    mouseXRef.current = (e.touches[0].clientX - rect.left) * scaleX;
  };
  const onTouchStart = (e) => {
    if (!e.touches?.[0]) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = W / rect.width;
    mouseXRef.current = (e.touches[0].clientX - rect.left) * scaleX;
    onLaunch();
  };
  const onTouchEnd = () => {
    /* leave paddle at last position */
  };

  return {
    isFullscreen,
    pointerLocked,
    toggleFullscreen,
    canvasHandlers: {
      onMouseMove,
      onClick,
      onMouseDown,
      onContextMenu,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
  };
}
